import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getUnits } from '../../redux/actions/unit';
import { getOrgBuildingSuites } from '../../redux/actions/building';
import { requestPending, requestSuccess } from '../../utils/status';
import { GET_COMMON_ACCESS, GET_ORG_BUILDING_SUITES, GET_UNITS } from '../../redux/constants'; // Updated constant
import { Palette } from '../../components/utils';
import { StyledTree } from '../../components/ui/StyledTree';
import { IoChevronDownSharp, IoLayersOutline } from 'react-icons/io5';
import { MdOutlineDoorSliding, MdOutlineMeetingRoom } from 'react-icons/md';
import { LOADING_BLUE } from '../../utils/colorCode';
import LoadingAvatar from '../../components/LoadingAvatar';
import { FaSearch } from 'react-icons/fa';
import {getCommonAccess, setCommonAccess} from '../../redux/actions/security';
import {LockProviders} from '../../utils/lockInfo';

const AllUnitsKey = 'all-units-container';
const AllDoorsKey = 'all-doors-container';
const NoRooms = {
  ID: 'no-rooms',
  NAME: 'No Rooms'
};
const ParentNodeCheckedStates = {
  NONE: 0,
  PARTIAL_SELECTED: 1,
  ALL_SELECTED: 2,
};

const UnitTreeCheckBoxes = ({
  buildingId,
  organizationId,
  onSelectionChange,
  buidlingConfig = {},
  initialSelectedUnitIds = [],
  initialSelectedSuiteIds = [],
  initialSelectedDoorIds = [],
  suiteEnabled,
  isSubmitting,
  searchTerm,
  showOnlyUnitsWithLock = false
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { units: allUnits, status: unitsStatus } = useSelector((state) => state.unit);
  const { allSuites, status: buildingSuitesStatus } = useSelector(
    (state) => state.building
  );
  const { getCommonAccessData, getCommonAccessStatus } = useSelector((state) => state.security);

  const [selectedUnitIds, setSelectedUnitIds] = useState(initialSelectedUnitIds);
  const [selectedSuiteIds, setSelectedSuiteIds] = useState(initialSelectedSuiteIds);
  const [selectedDoorIds, setSelectedDoorIds] = useState(initialSelectedDoorIds);
  const [selectedFloorIds, setSelectedFloorIds] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([AllUnitsKey]);

  const [unitsToSuiteMap, setUnitsToSuiteMap] = useState({});
  const [unitsToFloorMap, setUnitsToFloorMap] = useState({});
  const [suitesToNoRoomsMap, setSuitesToNoRoomsMap] = useState({});
  const [includeDoors, setIncludeDoors] = useState(false);

  const isUnitsLoading = unitsStatus === requestPending(GET_UNITS);
  const isSuitesLoading =
    suiteEnabled && buildingSuitesStatus === requestPending(GET_ORG_BUILDING_SUITES);
  const isLoading = isUnitsLoading || isSuitesLoading;

  const filterTreeData = useCallback((data, term) => {
    if (!term.trim()) return data;
    
    const lowerTerm = term.toLowerCase();
    
    const filterNode = (node) => {
      const newNode = { ...node };
      
      let titleText = '';
      try {
        if (newNode.isUnit && newNode.title?.props?.children) {
          titleText = String(newNode.title.props.children);
        } else if (newNode.isSuite && newNode.title?.props?.children?.props?.children?.[2]) {
          titleText = String(newNode.title.props.children.props.children[2]);
        } else if (newNode.title?.props?.children?.[1]) {
          titleText = String(newNode.title.props.children[1]);
        }
      } catch (e) {
        console.warn('Error extracting title text:', e);
      }
      
      const matches = titleText.toLowerCase().includes(lowerTerm);
      
      if (newNode.children) {
        newNode.children = newNode.children
          .map(filterNode)
          .filter(Boolean);
        
        return matches || newNode.children.length > 0 ? newNode : null;
      }
      
      return matches ? newNode : null;
    };
    
    return data.map(filterNode).filter(Boolean);
  }, []);

  const buildUnitMaps = (unitsByFloor, enableSuites) => {
    const unitToSuiteMap = {};
    const unitToFloorMap = {};
    const suiteToNoRoomMap = {}
  
    Object.entries(unitsByFloor).forEach(([floorIdStr, floorData]) => {
      const floorId = parseInt(floorIdStr, 10);
  
      if (enableSuites) {
        if (floorData && floorData.suites) {
          floorData.suites.forEach(({ suite, units = [] }) => {
            if (units.length == 0) {
              suiteToNoRoomMap[suite.id] = NoRooms.ID;
              return;
            }
            units.forEach((unit) => {
              unitToSuiteMap[unit.id] = suite.id;
              unitToFloorMap[unit.id] = floorId;
            });
          });
        }
      } else {
        if (floorData && floorData.units) {
          floorData.units.forEach((unit) => {
            unitToFloorMap[unit.id] = floorId;
            unitToSuiteMap[unit.id] = unit.suite_id;
          });
        }
      }
    });
  
    return { unitToSuiteMap, unitToFloorMap, suiteToNoRoomMap };
  };

  const fetchInitialData = useCallback(() => {
    if (buildingId) {
      dispatch(
        getUnits({
          id: parseInt(buildingId)
        })
      );

      if (organizationId && suiteEnabled) {
        dispatch(
          getOrgBuildingSuites({
            orgId: organizationId,
            buildingId
          })
        );
      }

      if(includeDoors) {
        dispatch(
          getCommonAccess({
            org_id: organizationId,
            building_id: buildingId
          })
        );
      }
    }
  }, [buildingId, organizationId, suiteEnabled, includeDoors, dispatch]);

  const groupUnitsByFloor = useCallback(() => {
    const unitsByFloor = {};
    const hasSuites =
      suiteEnabled &&
      buildingSuitesStatus === requestSuccess(GET_ORG_BUILDING_SUITES) && // Updated
      allSuites?.length > 0;

    if (hasSuites) {
      allSuites
        // If suite has no locks, ignore rooms
        .filter((suite) => suite.paired_devices?.length > 0)
        .forEach((suite) => {
          const suiteUnitIds = suite.rooms_devices.map(device => device.unit_id);

          const unitsInSuite = (allUnits || []).filter((u) => suiteUnitIds.includes(u.id));
 
          const suiteFloors = new Set(
            unitsInSuite.map((unit) => unit.floor)
          );

          if (suiteFloors.size === 0) {
            suiteFloors.add(NoRooms.ID);
          }

          suiteFloors.forEach((floor) => {
            unitsByFloor[floor] = unitsByFloor[floor] || { units: [], suites: [] };
            if (!unitsByFloor[floor].suites.some((s) => s.suite.id === suite.id)) {
              unitsByFloor[floor].suites.push({
                suite,
                units: unitsInSuite
                  .filter((unit) => unit.floor === floor)
                  .sort((a, b) => a.unit_num.localeCompare(b.unit_num))
              });
            }
          });
        });
    } else {
      (allUnits || []).forEach((unit) => {
        if (!showOnlyUnitsWithLock || unit.lock_id) {
          unitsByFloor[unit.floor] = unitsByFloor[unit.floor] || { units: [], suites: [] };
          if (!unit.suite_id || !hasSuites) {
            unitsByFloor[unit.floor].units.push(unit);
          }
        }
      });
    }

    const {unitToSuiteMap = {}, unitToFloorMap = {}, suiteToNoRoomMap = {}} = buildUnitMaps(unitsByFloor, suiteEnabled);
    setUnitsToSuiteMap(unitToSuiteMap);
    setUnitsToFloorMap(unitToFloorMap);
    setSuitesToNoRoomsMap(suiteToNoRoomMap);

    return unitsByFloor;
  }, [allUnits, allSuites, suiteEnabled, buildingSuitesStatus, showOnlyUnitsWithLock]);

  const buildTreeData = useCallback(
    (unitsByFloor, doors) => {
      // Floors
      const floorNodes = Object.keys(unitsByFloor)
        .sort((a, b) => (a === NoRooms.ID ? 1 : parseInt(a) - parseInt(b)))
        .map((floor) => {
          const floorChildren = [
            ...unitsByFloor[floor].suites
              .sort((a, b) => a.suite.name.localeCompare(b.suite.name))
              .map(({ suite, units }) => ({
                key: `suite-${suite.id}`,
                title: (
                  <TitleContainer>
                    <TreeNodeTitle style={{ marginLeft: '30px' }}>
                      {units.length > 0 && (
                        <IoChevronDownSharp
                          style={{
                            transform: expandedKeys.includes(`suite-${suite.id}`)
                              ? 'rotate(0deg)'
                              : 'rotate(-90deg)',
                            transition: 'transform 0.2s',
                            marginRight: '5px'
                          }}
                        />
                      )}
                      <SuiteIcon />
                      {`Suite ${suite.name}`}
                    </TreeNodeTitle>
                  </TitleContainer>
                ),
                checkable: true,
                isSuite: true,
                children: units.map((unit) => ({
                  key: `unit-${unit.id}-${unit.suite_id}`,
                  title: (
                    <TreeNodeTitle style={{ marginLeft: '60px' }}>
                      <RoomIcon />
                      {`Room ${unit.unit_num}`}
                    </TreeNodeTitle>
                  ),
                  isUnit: true,
                  checkable: true
                }))
              })),
            ...unitsByFloor[floor].units
              .sort((a, b) => a.unit_num.localeCompare(b.unit_num))
              .map((unit) => ({
                key: `unit-${unit.id}-${unit.suite_id}`,
                title: (
                  <TreeNodeTitle style={{ marginLeft: '40px' }}>
                    <SuiteIcon />
                    {`Unit ${unit.unit_num}`}
                  </TreeNodeTitle>
                ),
                isUnit: true,
                checkable: true
              }))
          ];

          return {
            key: `floor-${floor}`,
            title: (
              <TitleContainer>
                <TreeNodeTitle style={{ marginLeft: '10px' }}>
                  {(unitsByFloor[floor].suites.length > 0 ||
                    unitsByFloor[floor].units.length > 0) && (
                    <IoChevronDownSharp
                      style={{
                        transform: expandedKeys.includes(`floor-${floor}`)
                          ? 'rotate(0deg)'
                          : 'rotate(-90deg)',
                        transition: 'transform 0.2s',
                        marginRight: '8px'
                      }}
                    />
                  )}
                  <FloorIcon />
                  {floor === NoRooms.ID ? t(NoRooms.NAME) : `Floor ${floor}`}
                </TreeNodeTitle>
              </TitleContainer>
            ),
            checkable: true,
            children: floorChildren
          };
        });

      // Doors
      const doorNodes = [...doors]
        .sort((a, b) => a.display_name.localeCompare(b.display_name, undefined, {numeric: true, sensitivity: 'base'}))
        .map((door) => {
          return {
            key: `door-${door.id}`,
            title: (
              <TitleContainer>
                <TreeNodeTitle style={{ marginLeft: '10px' }}>
                  <FloorIcon />
                  {door.display_name}
                </TreeNodeTitle>
              </TitleContainer>
            ),
            checkable: true,
            children: []
          };
        });

      const floorsTree = floorNodes.length > 0
        ? [
            {
              key: AllUnitsKey,
              title: (
                <TitleContainer>
                  <TreeNodeTitle style={{ fontWeight: 'bold' }}>
                    {floorNodes.length > 0 && (
                      <IoChevronDownSharp
                        style={{
                          transform: expandedKeys.includes(AllUnitsKey)
                            ? 'rotate(0deg)'
                            : 'rotate(-90deg)',
                          transition: 'transform 0.2s',
                          marginRight: '8px'
                        }}
                      />
                    )}
                    {t('All Units')}
                  </TreeNodeTitle>
                </TitleContainer>
              ),
              checkable: true,
              children: floorNodes
            }
          ]
        : [];

      const doorsTree = doorNodes.length > 0
        ? [
            {
              key: AllDoorsKey,
              title: (
                <TitleContainer>
                  <TreeNodeTitle style={{ fontWeight: 'bold' }}>
                    {doorNodes.length > 0 && (
                      <IoChevronDownSharp
                        style={{
                          transform: expandedKeys.includes(AllDoorsKey)
                            ? 'rotate(0deg)'
                            : 'rotate(-90deg)',
                          transition: 'transform 0.2s',
                          marginRight: '8px'
                        }}
                      />
                    )}
                    {t('All Common Doors')}
                  </TreeNodeTitle>
                </TitleContainer>
              ),
              checkable: true,
              children: doorNodes
            }
          ]
        : [];

      return [...floorsTree, ...doorsTree];
    },
    [t, selectedUnitIds, selectedSuiteIds, expandedKeys]
  );

  const getNodeId = (key) => {
    return parseInt(key.split('-')[1], 10);
  };

  const updateTreeData = useCallback(() => {
    const isDataReady = 
      unitsStatus === requestSuccess(GET_UNITS) &&
      (!suiteEnabled || buildingSuitesStatus === requestSuccess(GET_ORG_BUILDING_SUITES)) &&
      (!includeDoors || getCommonAccessStatus === requestSuccess(GET_COMMON_ACCESS));

    if (isDataReady) {
      const unitsByFloor = groupUnitsByFloor();
      const allDoors = getCommonAccessData;

      const newTreeData = buildTreeData(unitsByFloor, allDoors);
      const filteredData = filterTreeData(newTreeData, searchTerm);

      setTreeData(filteredData);
    }
  }, [
    unitsStatus, 
    suiteEnabled,
    includeDoors, 
    buildingSuitesStatus, 
    getCommonAccessData, 
    groupUnitsByFloor, 
    buildTreeData, 
    searchTerm, 
    filterTreeData
  ]);
 
  const handleCheck = useCallback(
    (checkedResult, event) => {
  
      const { node, checked: isChecked } = event;
      const key = node.key;
      // Units
      const isAllUnits = key === AllUnitsKey;
      const isFloor = key.startsWith("floor-");
      const isSuite = key.startsWith("suite-");
      const isUnit = key.startsWith("unit-");
      // Doors
      const isAllDoors = key === AllDoorsKey;
      const isDoor = key.startsWith("door-");
  
      const handleAllUnitsSelection = (isChecked) => {
        if (isChecked) {
          const unitIds = [...new Set(Object.keys(unitsToFloorMap).map(Number))];
          const floorIds = [
            ...new Set(Object.values(unitsToFloorMap).map(Number)), 
            ...new Set(Object.values(suitesToNoRoomsMap))
          ];
          const suiteIds = [...new Set(Object.values(unitsToSuiteMap).map(Number))];
          const suitesNoRoom = [...new Set(Object.keys(suitesToNoRoomsMap).map(Number))];
  
          setSelectedUnitIds(unitIds);
          setSelectedSuiteIds([...suiteIds, ...suitesNoRoom]);
          setSelectedFloorIds(floorIds);
        } else {
          setSelectedUnitIds([]);
          setSelectedSuiteIds([]);
          setSelectedFloorIds([]);
        }
      };

      const handleAllDoorsSelection = (isChecked) => {
        if (isChecked) {
          const doorIds = [...new Set(getCommonAccessData.map(d => d?.id).filter(Boolean))];
          setSelectedDoorIds(doorIds);
        } else {
          setSelectedDoorIds([]);
        }
      };
  
      const handleFloorSelection = (isChecked, floorId) => {
        const unitIds = Object.entries(unitsToFloorMap)
          .map(([unitID, floorID]) => (floorId === Number(floorID) ? Number(unitID) : undefined))
          .filter(Boolean);
  
        let suiteIds = [];
        if (suiteEnabled) {
          if (floorId == NoRooms.ID) {
            suiteIds = Object.keys(suitesToNoRoomsMap).map(Number);
          } else {
            suiteIds = Object.entries(unitsToSuiteMap)
            .map(([unitID, suiteID]) => (unitIds.includes(Number(unitID)) ? Number(suiteID) : undefined))
            .filter(Boolean);
          }
        }

        if (isChecked) {
          setSelectedUnitIds((prev) => [...new Set([...prev, ...unitIds])]);
          setSelectedSuiteIds((prev) => [...new Set([...prev, ...suiteIds])]);
          setSelectedFloorIds((prev) => [...new Set([...prev, floorId])]);
        } else {
          setSelectedUnitIds((prev) => prev.filter((id) => !unitIds.includes(id)));
          setSelectedSuiteIds((prev) => prev.filter((id) => !suiteIds.includes(id)));
          setSelectedFloorIds((prev) => prev.filter((id) => id !== floorId));
        }
      };
  
      const handleSuiteSelection = (isChecked, suiteId) => {
        if (isChecked) {
          setSelectedSuiteIds((prev) => [...new Set([...prev, suiteId])]);
        } else {
          setSelectedSuiteIds((prev) => prev.filter((id) => id !== suiteId));
          // clear units
          const unitIds = getSuiteUnitsIds(suiteId);
          setSelectedUnitIds((prev) => prev.filter((id) => !unitIds.includes(id)));
        }
      };
  
      const handleUnitSelection = (isChecked, unitId) => {
        if (isChecked) {
          setSelectedUnitIds((prev) => [...new Set([...prev, unitId])]);
          if (suiteEnabled) {
            const suiteId = getUnitSuiteId(unitId);
            setSelectedSuiteIds((prev) => [...new Set([...prev, Number(suiteId)])]);
          }
        } else {
          setSelectedUnitIds((prev) => prev.filter((id) => id !== unitId));
        }
      };

      const handleDoorSelection = (isChecked, doorId) => {
        if (isChecked) {
          setSelectedDoorIds((prev) => [...new Set([...prev, doorId])]);
        } else {
          setSelectedDoorIds((prev) => prev.filter((id) => id !== doorId));
        }
      };
  
      // route
      if (isAllUnits) {
        handleAllUnitsSelection(isChecked);
      } else if (isFloor) {
        const isNoRoom = key === `floor-${NoRooms.ID}`;
        const floorId = isNoRoom ? NoRooms.ID : getNodeId(key);
        handleFloorSelection(isChecked, floorId);
      } else if (isSuite) {
        const suiteId = getNodeId(key);
        handleSuiteSelection(isChecked, suiteId);
      } else if (isUnit) {
        const unitId = getNodeId(key);
        handleUnitSelection(isChecked, unitId);
      } else if (isAllDoors) {
        handleAllDoorsSelection(isChecked);
      } else if (isDoor) {
        const doorId = getNodeId(key);
        handleDoorSelection(isChecked, doorId);
      }
    },
    [
      onSelectionChange, 
      unitsToSuiteMap,
      unitsToFloorMap, 
      suitesToNoRoomsMap, 
      suiteEnabled, 
      getCommonAccessData
    ]
  );  

  const handleExpand = useCallback((newExpandedKeys) => {
    setExpandedKeys(newExpandedKeys);
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    updateTreeData();
  }, [updateTreeData]);

  useEffect(() => {
    setSelectedUnitIds(initialSelectedUnitIds);
  }, [initialSelectedUnitIds]);

  useEffect(() => {
    if (suiteEnabled) {
      setSelectedSuiteIds(initialSelectedSuiteIds);
    }
  }, [initialSelectedSuiteIds, suiteEnabled]);

  useEffect(() => {
    setSelectedDoorIds(initialSelectedDoorIds);
  }, [initialSelectedDoorIds]);

  useEffect(() => {
    if(!!buidlingConfig) {
      setIncludeDoors([
        LockProviders.SALTO,
        LockProviders.SALTO_ALFRED, 
        LockProviders.ALFRED,
        LockProviders.YALE
      ].includes(buidlingConfig?.lock_provider));
    }
    return () => {
      dispatch(
        setCommonAccess([])
      );
    };
  }, [buidlingConfig, setIncludeDoors]);

  const getSuiteUnitsIds = useCallback((suiteId) => {
    return Object.entries(unitsToSuiteMap)
      .filter(([_, suiteID]) => suiteId == suiteID)
      .map(([unitID]) => Number(unitID));
  }, [unitsToSuiteMap]);

  const getUnitSuiteId = useCallback((unitId) => {
    return unitsToSuiteMap[`${unitId}`];
  }, [unitsToSuiteMap]);

  const getSelectionState = useCallback((unitIds, suiteIds, floorIds) => {
    const allUnitIds = Object.keys(unitsToSuiteMap).map(Number);
    const allFloorIds = [
      ...new Set(Object.values(unitsToFloorMap)).map(Number), 
      ...new Set(Object.values(suitesToNoRoomsMap)), 
    ];

    const floorStates = {};
  
    allFloorIds.forEach(floorId => {
      const isNoRoomsFloor = floorId == NoRooms.ID;
      const unitsForFloor = allUnitIds.filter(unitId => unitsToFloorMap[unitId] === floorId);
      const allNoRoomsSuites = [...new Set(Object.keys(suitesToNoRoomsMap))].map(Number);
      const selectedNoRoomsSuites = allNoRoomsSuites.filter(id => suiteIds.includes(id));

      let suitesForFloor = [...new Set(unitsForFloor.map(unitId => unitsToSuiteMap[unitId])).map(Number)];

      if (isNoRoomsFloor) {
        suitesForFloor = [...suitesForFloor, ...allNoRoomsSuites];
      }
  
      const selectedUnits = unitsForFloor.filter(unitId => unitIds.includes(unitId));
      const isAllUnitsSelected = selectedUnits.length > 0 && selectedUnits.length === unitsForFloor.length;
      const isAllNoRoomSuitesSelected = selectedNoRoomsSuites.length > 0 && selectedNoRoomsSuites.length === allNoRoomsSuites.length;
  
      const hasSelectedSuite = suitesForFloor.some(suiteId => suiteIds.includes(suiteId));
  
      if (isAllUnitsSelected || (isNoRoomsFloor && isAllNoRoomSuitesSelected)) {
        floorStates[floorId] = ParentNodeCheckedStates.ALL_SELECTED;
      } else if (selectedUnits.length > 0 || hasSelectedSuite) {
        floorStates[floorId] = ParentNodeCheckedStates.PARTIAL_SELECTED;
      } else {
        floorStates[floorId] = ParentNodeCheckedStates.NONE;
      }
    });
  
    const isAllFloorsSelected = Object.values(floorStates).every(
      state => state === ParentNodeCheckedStates.ALL_SELECTED
    );
  
    let overallSelectionState = ParentNodeCheckedStates.NONE;
  
    if (isAllFloorsSelected) {
      overallSelectionState = ParentNodeCheckedStates.ALL_SELECTED;
    } else if (unitIds.length > 0 || suiteIds.length > 0 || floorIds.length > 0) {
      overallSelectionState = ParentNodeCheckedStates.PARTIAL_SELECTED;
    }

    return {
      floorStates,
      overallSelectionState
    };
  }, [unitsToSuiteMap, unitsToFloorMap, suitesToNoRoomsMap]);

  const getCheckedKeyFromIds = useCallback((unitIds, suiteIds, floorIds, doorIds) => {
    const unitsKeys = unitIds.map(id => (`unit-${id}-${getUnitSuiteId(id)}`));
    const suitesKeys = suiteIds.map(id => (`suite-${id}`));
    const floorsKeys = floorIds.map(id => (`floor-${id}`));
    const doorsKeys = doorIds.map(id => (`door-${id}`));
    let checkedKeys = [...unitsKeys, ...suitesKeys, ...floorsKeys, ...doorsKeys];
    let halfCheckedKeys = [];

    const {floorStates, overallSelectionState} = getSelectionState(unitIds, suiteIds, floorIds);
 
    // Update All Units selection state
    switch (overallSelectionState) {
      case ParentNodeCheckedStates.ALL_SELECTED:
        halfCheckedKeys = halfCheckedKeys.filter(key => key !== AllUnitsKey);
        checkedKeys.push(AllUnitsKey);
        break;
      case ParentNodeCheckedStates.PARTIAL_SELECTED:
        checkedKeys = checkedKeys.filter(key => key !== AllUnitsKey);
        halfCheckedKeys.push(AllUnitsKey);
        break;
      case ParentNodeCheckedStates.NONE:
        halfCheckedKeys = halfCheckedKeys.filter(key => key !== AllUnitsKey);
        checkedKeys = checkedKeys.filter(key => key !== AllUnitsKey);
        break;
    }

    // Update Floors selection state
    Object.entries(floorStates).forEach(([floorId, state])=>{
      const floorKey = `floor-${floorId}`;
      switch (state) {
        case ParentNodeCheckedStates.ALL_SELECTED:
          halfCheckedKeys = halfCheckedKeys.filter(key => key !== floorKey);
          checkedKeys.push(floorKey);
          break;
        case ParentNodeCheckedStates.PARTIAL_SELECTED:
          checkedKeys = checkedKeys.filter(key => key !== floorKey);
          halfCheckedKeys.push(floorKey);
          break;
        case ParentNodeCheckedStates.NONE:
          halfCheckedKeys = halfCheckedKeys.filter(key => key !== floorKey);
          checkedKeys = checkedKeys.filter(key => key !== floorKey);
          break;
      }
    })

    // Update All Doors selection state
    if (doorIds.length > 0) {
      const targetKeys = (getCommonAccessData.length === doorIds.length) 
        ? checkedKeys 
        : halfCheckedKeys;

      targetKeys.push(AllDoorsKey);
    }

    return {
      checked: checkedKeys,
      halfChecked: halfCheckedKeys,
    }
  },[
    unitsToFloorMap, 
    unitsToSuiteMap, 
    suitesToNoRoomsMap
  ]);

  const currentCheckedKeys = useMemo(()=>{
    const suites = suiteEnabled ? selectedSuiteIds : [];
    onSelectionChange(selectedUnitIds, suites, selectedDoorIds);

    const {checked, halfChecked} = getCheckedKeyFromIds(
      selectedUnitIds, 
      selectedSuiteIds, 
      selectedFloorIds, 
      selectedDoorIds
    );

    return {
      checked,
      halfChecked,
    }
  },[
    selectedUnitIds, 
    selectedSuiteIds, 
    selectedFloorIds, 
    selectedDoorIds, 
    unitsToFloorMap, 
    unitsToSuiteMap, 
    suitesToNoRoomsMap
  ]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <LoadingAvatar color={LOADING_BLUE} />
          <LoadingText>{t('Loading units...')}</LoadingText>
        </LoadingContainer>
      );
    }

    return (
      <>
        {treeData?.length > 0 ? (
          <StyledTree
            disabled={isSubmitting}
            checkable
            checkStrictly
            treeData={treeData}
            onCheck={handleCheck}
            checkedKeys={currentCheckedKeys}
            onExpand={handleExpand}
            expandedKeys={expandedKeys}
            switcherIcon={<CustomSwitcherIcon />}
            showIcon={false}
            className="custom-tree"
          />
        ) : (
          <NoDataMessage>
            {showOnlyUnitsWithLock
              ? t('No units with locks available for this building.')
              : t('No floors or suites available for this building.')}
          </NoDataMessage>
        )}
      </>
    );
  };

  return <div>{renderContent()}</div>;
};

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const LoadingText = styled.span`
  margin-top: 10px;
  text-align: center;
`;

const CustomSwitcherIcon = styled(IoChevronDownSharp)`
  opacity: 0;
`;

const Badge = styled.span`
  background-color: #1890ff;
  color: white !important;
  border-radius: 100%;
  padding: 0 9px;
  font-size: 12px;
  margin-left: 8px;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TreeNodeTitle = styled.span`
  display: flex;
  align-items: center;
`;

const FloorIcon = styled(IoLayersOutline)`
  margin-right: 8px;
  color: ${Palette.BLACK_50};
`;

const SuiteIcon = styled(MdOutlineDoorSliding)`
  margin-right: 5px;
  color: ${Palette.BLACK_50};
`;

const RoomIcon = styled(MdOutlineMeetingRoom)`
  margin-right: 5px;
  color: ${Palette.BLACK_50};
`;

const NoDataMessage = styled.p`
  padding-left: 10px;
  padding-top: 10px;
  min-height: 40px;
  color: ${Palette.BLACK_50}; 
`;


export default UnitTreeCheckBoxes;
